/**
 * Create Client Request Page
 *
 * Multi-step form for clients (buyers/renters) to submit property requests.
 * Brokers can then view and respond to these requests.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Stepper from '../../components/base/Stepper';
import OptionTile, { OptionTileGroup } from '../../components/base/OptionTile';
import PhoneInput from '../../components/base/PhoneInput';
import DomainIcon from '../../components/base/icons/domain/DomainIcon';
import CityCombobox from '../../components/search/CityCombobox';
import { getPropertyTypeIconId } from '../../config/propertyTypeCatalog';
import { getVisibleFieldGroups } from '../../config/propertyTypeConfig';
import type { ResolvedSaudiCity } from '../../hooks/useSaudiCities';
import { useAuth } from '../../hooks/useAuth';
import { createClientRequest } from '../../services/clientRequestService';

// Types
interface ClientRequestData {
  // Step 1: Request Type
  requestType: 'buy' | 'rent';
  // Phase 7 typed demand board (شراء / استئجار / استثمار) — gated, additive.
  demandType?: 'PURCHASE' | 'RENTAL' | 'INVESTMENT';
  propertyType: string;

  // Step 2: Location — a city from the canonical list (config/saudiCities); its
  // database `cityId` is attached once GET /locations/cities answers.
  city: ResolvedSaudiCity | null;
  districts: string[];

  // Step 3: Budget & Specs
  budgetMin: number;
  budgetMax: number;
  bedrooms: number;
  bathrooms: number;
  minArea: number;

  // Step 4: Details
  moveInDate: string;
  rentalDuration?: string;
  specialRequirements: string;
  isPrivate: boolean;

  // Step 5: Contact
  name: string;
  phone: string;
  email: string;
  preferredContact: 'phone' | 'whatsapp' | 'email';
}

const initialData: ClientRequestData = {
  requestType: 'rent',
  propertyType: '',
  city: null,
  districts: [],
  budgetMin: 0,
  budgetMax: 0,
  bedrooms: 1,
  bathrooms: 1,
  minArea: 0,
  moveInDate: '',
  rentalDuration: '',
  specialRequirements: '',
  isPrivate: false,
  name: '',
  phone: '',
  email: '',
  preferredContact: 'whatsapp',
};

// Icons come from the domain icon registry (the same glyphs as the search type
// picker and the listing form), keyed by the lowercase id.
const propertyTypes = [
  { value: 'apartment', label: 'Apartment', labelAr: 'شقة' },
  { value: 'villa', label: 'Villa', labelAr: 'فيلا' },
  { value: 'floor', label: 'Floor', labelAr: 'دور' },
  { value: 'land', label: 'Land', labelAr: 'أرض' },
  { value: 'office', label: 'Office', labelAr: 'مكتب' },
  { value: 'shop', label: 'Shop', labelAr: 'محل' },
];

const rentalDurations = [
  { value: 'monthly', label: 'Monthly', labelAr: 'شهري' },
  { value: '3months', label: '3 Months', labelAr: '3 أشهر' },
  { value: '6months', label: '6 Months', labelAr: '6 أشهر' },
  { value: 'yearly', label: 'Yearly', labelAr: 'سنوي' },
];

// Phase 7 typed demand boards (طلب شراء / استئجار / استثمار) — gated by
// VITE_REQUEST_BOARDS_ENABLED. Maps to ClientRequest.demandType on the backend.
const demandTypes = [
  { value: 'PURCHASE' as const, label: 'Purchase', labelAr: 'شراء', icon: 'ri-shopping-bag-3-line' },
  { value: 'RENTAL' as const, label: 'Rental', labelAr: 'استئجار', icon: 'ri-key-2-line' },
  { value: 'INVESTMENT' as const, label: 'Investment', labelAr: 'استثمار', icon: 'ri-line-chart-line' },
];
const REQUEST_BOARDS_ENABLED = import.meta.env.VITE_REQUEST_BOARDS_ENABLED === 'true';

/** «النخيل، الملقا» or "Al Nakheel, Al Malqa" → one entry per district (Arabic or Latin comma). */
function splitDistricts(text: string): string[] {
  return text
    .split(/[,،]/)
    .map((district) => district.trim())
    .filter(Boolean);
}

// Preferred-contact tiles. Labels reuse the contact section's existing keys.
const contactMethods = [
  { value: 'phone' as const, icon: 'ri-phone-line', labelKey: 'contact.contactCall', fallback: 'مكالمة هاتفية' },
  { value: 'whatsapp' as const, icon: 'ri-whatsapp-line', labelKey: 'contact.contactWhatsapp', fallback: 'واتساب' },
  { value: 'email' as const, icon: 'ri-mail-line', labelKey: 'contact.email', fallback: 'البريد الإلكتروني' },
];

export default function CreateClientRequest() {
  const { t, i18n } = useTranslation('clientRequests');
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const { session } = useAuth();

  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<ClientRequestData>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Room counts the chosen type has (land: neither; office, shop: bathrooms only),
  // from the same per-type rules as the search filters (config/propertyTypeConfig).
  // A count the type does not have is neither shown nor sent, so a land request is
  // never stored as "at least 1 bedroom". Before a type is picked, both show.
  const typeFields = formData.propertyType ? getVisibleFieldGroups(formData.propertyType) : null;
  const askBedrooms = typeFields ? typeFields.bedrooms : true;
  const askBathrooms = typeFields ? typeFields.bathrooms : true;

  const steps = [
    { id: 'type', label: t('steps.type', 'نوع الطلب') },
    { id: 'location', label: t('steps.location', 'الموقع') },
    { id: 'budget', label: t('steps.budget', 'الميزانية') },
    { id: 'details', label: t('steps.details', 'التفاصيل') },
    { id: 'contact', label: t('steps.contact', 'التواصل') },
  ];

  const updateFormData = (updates: Partial<ClientRequestData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // The districts field keeps the raw text, so separators and spaces survive while
  // typing («الملك فهد، الملقا»); the list is parsed from it on every change.
  const [districtsText, setDistrictsText] = useState('');
  const handleDistrictsChange = (text: string) => {
    setDistrictsText(text);
    updateFormData({ districts: splitDistricts(text) });
  };

  // A different city clears the districts typed for the old one. The picker also
  // hands back the SAME city once its database id has loaded; that keeps them.
  const handleCityChange = (city: ResolvedSaudiCity | null) => {
    if (formData.city?.slug !== city?.slug) setDistrictsText('');
    setFormData((prev) => ({
      ...prev,
      city,
      districts: prev.city?.slug === city?.slug ? prev.districts : [],
    }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitError('');
    // Minimal client-side guard — the backend validates the rest (email/phone).
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      setSubmitError(t('errors.missingContact', 'يرجى تعبئة الاسم والبريد الإلكتروني ورقم الجوال'));
      setCurrentStep(steps.length - 1); // jump to the contact step
      return;
    }
    if (!formData.propertyType) {
      setSubmitError(t('errors.missingType', 'يرجى اختيار نوع العقار'));
      setCurrentStep(0);
      return;
    }
    setIsSubmitting(true);
    try {
      const { city } = formData;
      const cityLabel = city ? (isRTL ? city.nameAr : city.nameEn) : undefined;
      const durOpt = rentalDurations.find((d) => d.value === formData.rentalDuration);
      const extraNotes = [
        formData.specialRequirements.trim(),
        durOpt ? `Rental: ${isRTL ? durOpt.labelAr : durOpt.label}` : '',
        formData.moveInDate ? `Move-in: ${formData.moveInDate}` : '',
        `Preferred contact: ${formData.preferredContact}`,
      ].filter(Boolean).join(' · ');

      await createClientRequest({
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        requestKind: formData.requestType,
        propertyType: formData.propertyType,
        demandType: formData.demandType,
        city: cityLabel,
        cityId: city?.cityId ?? undefined,
        districts: formData.districts,
        budgetMin: formData.budgetMin || undefined,
        budgetMax: formData.budgetMax || undefined,
        bedrooms: askBedrooms ? formData.bedrooms || undefined : undefined,
        bathrooms: askBathrooms ? formData.bathrooms || undefined : undefined,
        minAreaSqm: formData.minArea || undefined,
        extraNotes,
      }, session?.token ?? null);
      navigate('/client-requests/success');
    } catch (error) {
      console.error('Error submitting request:', error);
      setSubmitError(t('errors.submit', 'تعذّر إرسال الطلب. يرجى المحاولة مرة أخرى.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Request Type
        return (
          <div className="space-y-6">
            {/* Buy/Rent Selection */}
            <div>
              <p id="cr-request-type-label" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('requestTypeLabel', 'ماذا تبحث عن؟')}
              </p>
              <OptionTileGroup role="radiogroup" aria-labelledby="cr-request-type-label" className="grid grid-cols-2 gap-3">
                <OptionTile
                  size="lg"
                  selected={formData.requestType === 'rent'}
                  onSelect={() => updateFormData({ requestType: 'rent' })}
                  icon="ri-key-line"
                  label={t('rent', 'إيجار')}
                />
                <OptionTile
                  size="lg"
                  selected={formData.requestType === 'buy'}
                  onSelect={() => updateFormData({ requestType: 'buy' })}
                  icon="ri-home-heart-line"
                  label={t('buy', 'شراء')}
                />
              </OptionTileGroup>
            </div>

            {/* Demand board (Phase 7) — purchase / rental / investment. Gated. */}
            {REQUEST_BOARDS_ENABLED && (
              <div>
                <p id="cr-demand-type-label" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                  {t('demandTypeLabel', 'نوع الطلب (لوحة الطلبات)')}
                </p>
                <OptionTileGroup
                  role="radiogroup"
                  aria-labelledby="cr-demand-type-label"
                  className="grid grid-cols-1 gap-2.5 sm:grid-cols-3"
                >
                  {demandTypes.map((dt) => (
                    <OptionTile
                      key={dt.value}
                      size="md"
                      selected={formData.demandType === dt.value}
                      onSelect={() => updateFormData({ demandType: dt.value })}
                      icon={dt.icon}
                      label={isRTL ? dt.labelAr : dt.label}
                    />
                  ))}
                </OptionTileGroup>
              </div>
            )}

            {/* Property Type Selection */}
            <div>
              <p id="cr-property-type-label" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('propertyTypeLabel', 'نوع العقار')}
              </p>
              <OptionTileGroup
                role="radiogroup"
                aria-labelledby="cr-property-type-label"
                className="grid grid-cols-2 gap-2.5 sm:grid-cols-3"
              >
                {propertyTypes.map((type) => (
                  <OptionTile
                    key={type.value}
                    size="md"
                    selected={formData.propertyType === type.value}
                    onSelect={() => updateFormData({ propertyType: type.value })}
                    icon={<DomainIcon id={getPropertyTypeIconId(type.value)} />}
                    label={isRTL ? type.labelAr : type.label}
                  />
                ))}
              </OptionTileGroup>
            </div>
          </div>
        );

      case 1: // Location
        return (
          <div className="space-y-6">
            {/* City Selection — every Saudi city, searchable («جده» finds جدة);
                sends the database cityId with the request once it is known. */}
            <CityCombobox
              id="cr-city"
              value={formData.city}
              onChange={handleCityChange}
              allowAll={false}
              label={t('cityLabel', 'المدينة')}
              placeholder={t('selectCity', 'اختر المدينة')}
            />

            {/* Districts Multi-select (simplified) */}
            <div>
              <label htmlFor="cr-districts" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('districtsLabel', 'الأحياء المفضلة')}
              </label>
              <input
                id="cr-districts"
                type="text"
                aria-describedby="cr-districts-help"
                value={districtsText}
                onChange={(e) => handleDistrictsChange(e.target.value)}
                placeholder={t('districtsPlaceholder', 'أدخل الأحياء مفصولة بفاصلة')}
                className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
              />
              <p id="cr-districts-help" className="text-xs text-[var(--pv-color-text-muted)] mt-1">
                {t('districtsHelp', 'مثال: النخيل، الملقا، الياسمين')}
              </p>
            </div>
          </div>
        );

      case 2: // Budget & Specs
        return (
          <div className="space-y-6">
            {/* Budget Range */}
            <div role="group" aria-labelledby="cr-budget-label">
              <p id="cr-budget-label" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('budgetLabel', 'الميزانية (ر.س)')}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="number"
                  aria-label={t('minBudget', 'الحد الأدنى')}
                  value={formData.budgetMin || ''}
                  onChange={(e) => updateFormData({ budgetMin: parseInt(e.target.value) || 0 })}
                  placeholder={t('minBudget', 'الحد الأدنى')}
                  className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
                />
                <input
                  type="number"
                  aria-label={t('maxBudget', 'الحد الأقصى')}
                  value={formData.budgetMax || ''}
                  onChange={(e) => updateFormData({ budgetMax: parseInt(e.target.value) || 0 })}
                  placeholder={t('maxBudget', 'الحد الأقصى')}
                  className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
                />
              </div>
            </div>

            {/* Bedrooms */}
            {askBedrooms && (
            <div>
              <label htmlFor="cr-bedrooms" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('bedroomsLabel', 'عدد غرف النوم')}
              </label>
              <select
                id="cr-bedrooms"
                value={formData.bedrooms >= 6 ? 6 : formData.bedrooms}
                onChange={(event) => updateFormData({ bedrooms: Number(event.target.value) })}
                className="w-full max-w-[10rem] rounded-xl border-2 border-[var(--pv-border-soft)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--pv-color-text)] transition-colors focus:border-[var(--pv-color-primary)] focus:outline-none"
              >
                {[1, 2, 3, 4, 5, 6].map((num) => (
                  <option key={num} value={num}>{num === 6 ? '6+' : num}</option>
                ))}
              </select>
            </div>
            )}

            {/* Bathrooms */}
            {askBathrooms && (
            <div>
              <label htmlFor="cr-bathrooms" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('bathroomsLabel', 'عدد دورات المياه')}
              </label>
              <select
                id="cr-bathrooms"
                value={formData.bathrooms >= 5 ? 5 : formData.bathrooms}
                onChange={(event) => updateFormData({ bathrooms: Number(event.target.value) })}
                className="w-full max-w-[10rem] rounded-xl border-2 border-[var(--pv-border-soft)] bg-white px-4 py-2.5 text-sm font-medium text-[var(--pv-color-text)] transition-colors focus:border-[var(--pv-color-primary)] focus:outline-none"
              >
                {[1, 2, 3, 4, 5].map((num) => (
                  <option key={num} value={num}>{num === 5 ? '5+' : num}</option>
                ))}
              </select>
            </div>
            )}

            {/* Minimum Area */}
            <div>
              <label htmlFor="cr-min-area" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('minAreaLabel', 'الحد الأدنى للمساحة (م²)')}
              </label>
              <input
                id="cr-min-area"
                type="number"
                value={formData.minArea || ''}
                onChange={(e) => updateFormData({ minArea: parseInt(e.target.value) || 0 })}
                placeholder={t('minAreaPlaceholder', 'مثال: 150')}
                className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
              />
            </div>
          </div>
        );

      case 3: // Details
        return (
          <div className="space-y-6">
            {/* Move-in Date */}
            <div>
              <label htmlFor="cr-move-in" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('moveInDateLabel', 'تاريخ الاستلام المتوقع')}
              </label>
              <input
                id="cr-move-in"
                type="date"
                value={formData.moveInDate}
                onChange={(e) => updateFormData({ moveInDate: e.target.value })}
                className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
              />
            </div>

            {/* Rental Duration (if rent) */}
            {formData.requestType === 'rent' && (
              <div>
                <label htmlFor="cr-rental-duration" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                  {t('rentalDurationLabel', 'مدة الإيجار')}
                </label>
                <select
                  id="cr-rental-duration"
                  value={formData.rentalDuration}
                  onChange={(e) => updateFormData({ rentalDuration: e.target.value })}
                  className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
                >
                  <option value="">{t('selectDuration', 'اختر المدة')}</option>
                  {rentalDurations.map((duration) => (
                    <option key={duration.value} value={duration.value}>
                      {isRTL ? duration.labelAr : duration.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Special Requirements */}
            <div>
              <label htmlFor="cr-special" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('specialRequirementsLabel', 'متطلبات خاصة')}
              </label>
              <textarea
                id="cr-special"
                value={formData.specialRequirements}
                onChange={(e) => updateFormData({ specialRequirements: e.target.value })}
                placeholder={t('specialRequirementsPlaceholder', 'مثال: قريب من مدرسة، مواقف سيارات، حديقة...')}
                rows={4}
                className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20 resize-none"
              />
            </div>

            {/* Private Request Toggle */}
            <div className="flex items-center justify-between p-4 bg-[var(--pv-color-surface-soft)] rounded-xl">
              <div>
                <p id="cr-private-label" className="font-medium text-[var(--pv-color-text)]">
                  {t('privateRequestLabel', 'طلب خاص')}
                </p>
                <p id="cr-private-desc" className="text-sm text-[var(--pv-color-text-muted)]">
                  {t('privateRequestDesc', 'لن يظهر طلبك علناً، فقط للوسطاء المعتمدين')}
                </p>
              </div>
              {/* A 2.75rem hit area (the touch-target minimum) around the 3rem × 1.5rem track. */}
              <button
                type="button"
                role="switch"
                aria-checked={formData.isPrivate}
                aria-labelledby="cr-private-label"
                aria-describedby="cr-private-desc"
                onClick={() => updateFormData({ isPrivate: !formData.isPrivate })}
                className="inline-flex h-11 w-14 shrink-0 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#004D40]/20"
              >
                <span
                  aria-hidden="true"
                  className={`flex h-6 w-12 items-center rounded-full transition-colors ${
                    formData.isPrivate ? 'bg-[var(--pv-color-primary)]' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`h-5 w-5 rounded-full bg-white shadow transition-transform ${
                    formData.isPrivate ? 'translate-x-6 rtl:-translate-x-6' : 'translate-x-0.5 rtl:-translate-x-0.5'
                  }`} />
                </span>
              </button>
            </div>
          </div>
        );

      case 4: // Contact
        return (
          <div className="space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="cr-name" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('nameLabel', 'الاسم')}
              </label>
              <input
                id="cr-name"
                type="text"
                autoComplete="name"
                value={formData.name}
                onChange={(e) => updateFormData({ name: e.target.value })}
                placeholder={t('namePlaceholder', 'أدخل اسمك')}
                className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="cr-phone" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('phoneLabel', 'رقم الجوال')}
              </label>
              <PhoneInput
                id="cr-phone"
                value={formData.phone}
                onChange={(value) => updateFormData({ phone: value })}
                placeholder={t('phonePlaceholder', '5xxxxxxxx')}
                required
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="cr-email" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('emailLabel', 'البريد الإلكتروني')}
              </label>
              <input
                id="cr-email"
                type="email"
                autoComplete="email"
                value={formData.email}
                onChange={(e) => updateFormData({ email: e.target.value })}
                placeholder={t('emailPlaceholder', 'example@email.com')}
                className="w-full px-4 py-3 bg-[var(--pv-color-surface-soft)] border border-[var(--pv-border-soft)] rounded-xl text-[var(--pv-color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--pv-color-primary)]/20"
              />
            </div>

            {/* Preferred Contact Method */}
            <div>
              <p id="cr-contact-method-label" className="block text-sm font-medium text-[var(--pv-color-text)] mb-3">
                {t('preferredContactLabel', 'طريقة التواصل المفضلة')}
              </p>
              <OptionTileGroup
                role="radiogroup"
                aria-labelledby="cr-contact-method-label"
                className="grid grid-cols-1 gap-2.5 sm:grid-cols-3"
              >
                {contactMethods.map((method) => (
                  <OptionTile
                    key={method.value}
                    size="md"
                    selected={formData.preferredContact === method.value}
                    onSelect={() => updateFormData({ preferredContact: method.value })}
                    icon={method.icon}
                    label={t(method.labelKey, method.fallback)}
                  />
                ))}
              </OptionTileGroup>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)]" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      {/* Page Header */}
      {/* The title and one line; no icon tile above it and no tinted strip
          behind it (owner, 2026-10-07: both read as an AI-made page). */}
      <header className="pb-4 pt-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-bold text-[var(--pv-color-primary)] mb-2">
            {t('createRequestTitle', 'أضف طلبك')}
          </h1>
          <p className="text-[var(--pv-color-text-muted)] max-w-lg mx-auto">
            {t('createRequestSubtitle', 'أخبرنا ما تبحث عنه وسيتواصل معك الوسطاء المعتمدون')}
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stepper */}
        <div className="mb-8">
          <Stepper
            steps={steps}
            currentStep={currentStep}
            ariaLabel={t('stepperLabel', 'خطوات إضافة الطلب')}
          />
        </div>

        {/* Form Card */}
        <Card variant="bordered" padding="lg">
          <form onSubmit={(e) => { e.preventDefault(); }}>
            {renderStepContent()}

            {submitError && (
              <div role="alert" className="mt-6 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm" dir={isRTL ? 'rtl' : 'ltr'}>
                {submitError}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-[var(--pv-border-soft)]">
              <button
                type="button"
                onClick={handlePrevious}
                disabled={currentStep === 0}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  currentStep === 0
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 cursor-not-allowed'
                    : 'bg-[var(--pv-color-surface-soft)] text-[var(--pv-color-text)] hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                <i className={`ri-arrow-${isRTL ? 'right' : 'left'}-line me-2`} aria-hidden="true"></i>
                {t('previous', 'السابق')}
              </button>

              {currentStep < steps.length - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-6 py-3 bg-[var(--pv-color-primary)] text-white rounded-xl font-medium hover:shadow-lg transition-all"
                >
                  {t('next', 'التالي')}
                  <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-line ms-2`} aria-hidden="true"></i>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  aria-busy={isSubmitting || undefined}
                  className="px-6 py-3 bg-[var(--pv-color-primary)] text-white rounded-xl font-medium hover:shadow-lg transition-all disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin me-2" aria-hidden="true"></i>
                      {t('submitting', 'جاري الإرسال...')}
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line me-2" aria-hidden="true"></i>
                      {t('submitRequest', 'إرسال الطلب')}
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </Card>
      </main>

      <Footer />
    </div>
  );
}
